/**
 * Currency-note-engraved EPC emblem for the app header.
 *
 * Sovereign Emerald register: two intaglio wind turbines whose rotors turn in
 * place, flanked by a small engraved ground-mount solar array (wind + solar
 * portfolio). Decorative only (`aria-hidden`), `pointer-events-none`.
 * All motion is wrapped in a `prefers-reduced-motion` guard.
 *
 * Technique ref: epc-cinematic-illustration skill.
 */

const CHAMPAGNE = '#C9A961';
const BOTTLE = '#1F5A47';
const BRAND = '#339274';

export default function EpcHeaderEmblem({
  className = '',
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      className={className}
      style={style}
      width="240"
      height="48"
      viewBox="0 0 240 48"
      fill="none"
      aria-hidden
    >
      <defs>
        {/* one engraved tilted solar panel, ground point at local (0,0) */}
        <g id="se-emblem-panel" fill="none" strokeLinecap="round">
          {/* mounting leg */}
          <line x1="0" y1="0" x2="0" y2="-4" />
          {/* tilted face */}
          <path d="M-7,-3.5 L7,-6.5 L7,-12 L-7,-9 Z" strokeLinejoin="round" />
          {/* cell grid */}
          <line x1="-2.33" y1="-4.5" x2="-2.33" y2="-10" strokeWidth="0.4" />
          <line x1="2.33" y1="-5.5" x2="2.33" y2="-11" strokeWidth="0.4" />
          <line x1="-7" y1="-6.25" x2="7" y2="-9.25" strokeWidth="0.4" />
        </g>
      </defs>

      {/* engraved dashed horizon (stops short of the notification bell) */}
      <line
        x1="6"
        y1="40"
        x2="162"
        y2="40"
        stroke={BOTTLE}
        strokeWidth="0.6"
        strokeDasharray="2 3"
        opacity="0.45"
        vectorEffect="non-scaling-stroke"
      />

      {/* ── distant solar panel (far left, echoes the distant turbine) ── */}
      <g transform="translate(38,40) scale(0.55)" stroke={BRAND} strokeWidth="0.9" opacity="0.5">
        <use href="#se-emblem-panel" />
      </g>

      {/* ── WIND TURBINE 2 (distant) ── */}
      <line x1="64" y1="40" x2="64" y2="27" stroke={BRAND} strokeWidth="0.9" strokeLinecap="round" opacity="0.6" vectorEffect="non-scaling-stroke" />
      <g className="se-emblem-rotor2">
        <line x1="64" y1="27" x2="64" y2="18.5" stroke={BRAND} strokeWidth="0.9" strokeLinecap="round" opacity="0.6" vectorEffect="non-scaling-stroke" />
        <line x1="64" y1="27" x2="71.4" y2="31.3" stroke={BRAND} strokeWidth="0.9" strokeLinecap="round" opacity="0.6" vectorEffect="non-scaling-stroke" />
        <line x1="64" y1="27" x2="56.6" y2="31.3" stroke={BRAND} strokeWidth="0.9" strokeLinecap="round" opacity="0.6" vectorEffect="non-scaling-stroke" />
      </g>
      <circle cx="64" cy="27" r="1.1" fill={BRAND} opacity="0.6" />

      {/* ── WIND TURBINE 1 (foreground) ── */}
      <line x1="96" y1="40" x2="96" y2="22" stroke={BOTTLE} strokeWidth="1.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <g className="se-emblem-rotor1">
        <line x1="96" y1="22" x2="96" y2="9" stroke={BOTTLE} strokeWidth="1.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <line x1="96" y1="22" x2="107.3" y2="28.5" stroke={BOTTLE} strokeWidth="1.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <line x1="96" y1="22" x2="84.7" y2="28.5" stroke={BOTTLE} strokeWidth="1.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </g>
      <circle cx="96" cy="22" r="1.5" fill={BOTTLE} />

      {/* ── engraved solar array (right of the turbines, stepping into the distance) ── */}
      <g transform="translate(124,40)" stroke={BOTTLE} strokeWidth="0.9" opacity="0.85">
        <use href="#se-emblem-panel" />
        {/* champagne sun-glint on the panel face */}
        <line className="se-emblem-glint" x1="-4.5" y1="-8" x2="3.5" y2="-9.7" stroke={CHAMPAGNE} strokeWidth="0.7" />
      </g>
      <g transform="translate(141,40) scale(0.8)" stroke={BOTTLE} strokeWidth="0.9" opacity="0.7">
        <use href="#se-emblem-panel" />
        <line className="se-emblem-glint2" x1="-4.5" y1="-8" x2="3.5" y2="-9.7" stroke={CHAMPAGNE} strokeWidth="0.7" />
      </g>
      <g transform="translate(155,40) scale(0.62)" stroke={BRAND} strokeWidth="0.9" opacity="0.55">
        <use href="#se-emblem-panel" />
      </g>

      <style>{`
        .se-emblem-rotor1{transform-box:view-box;transform-origin:96px 22px;animation:seEmblemRotor 6s linear infinite;}
        .se-emblem-rotor2{transform-box:view-box;transform-origin:64px 27px;animation:seEmblemRotor 4.5s linear infinite;}
        .se-emblem-glint{animation:seEmblemGlint 4s ease-in-out infinite alternate;}
        .se-emblem-glint2{animation:seEmblemGlint 4s ease-in-out 2s infinite alternate;}
        @keyframes seEmblemRotor{to{transform:rotate(360deg);}}
        @keyframes seEmblemGlint{from{opacity:0.15;}to{opacity:0.9;}}
        @media (prefers-reduced-motion:reduce){
          .se-emblem-rotor1,.se-emblem-rotor2,.se-emblem-glint,.se-emblem-glint2{animation:none;}
          .se-emblem-glint,.se-emblem-glint2{opacity:0.5;}
        }
      `}</style>
    </svg>
  );
}
